/**
 * English strings — the source of truth for keys. `bg.ts` must mirror this shape exactly
 * (enforced by the `Translation` type). Keep keys semantic, not positional.
 */
export const en = {
  app: {
    name: 'Kasora',
    untitledProject: 'Untitled project',
    projectMenu: 'Project menu',
    projects: 'Projects',
    renameProject: 'Rename project',
  },
  views: {
    label: 'Views',
    overview: 'Overview',
    cashFlow: 'Cash flow',
    balance: 'Balance',
    debt: 'Debt',
    risk: 'Risk',
    compare: 'Compare',
    ledger: 'Ledger',
    returns: 'Returns',
  },
  valueMode: {
    label: 'Value mode',
    nominal: 'Nominal',
    real: 'Real',
    nominalHint: 'Amounts as they will appear in each year',
    realHint: 'Amounts in today’s money, adjusted for inflation',
  },
  simMode: {
    label: 'Simulation mode',
    deterministic: 'Deterministic',
    monteCarlo: 'Monte Carlo',
    deterministicHint: 'One path with every rate at its mean',
    monteCarloHint: 'Many random paths shown as ranges of outcomes',
  },
  variants: {
    dropShare: 'Also use in {{name}}',
    dropCopy: 'Copy to {{name}}',
    title: 'Variants',
    newVariant: 'New variant',
    defaultName: 'Variant {{n}}',
    emptyTitle: 'No variants yet',
    emptyBody: 'A variant is one version of your plan, such as “Rent” or “Buy”.',
    createFirst: 'Create a variant',
    sharedItems: 'Shared items',
    assumptions: 'Assumptions',
    addItem: 'Add item',
    actions: 'Variant actions',
    edit: 'Edit variant',
    duplicateShared: 'Duplicate, sharing items',
    duplicateCopy: 'Duplicate, copying items',
    copyName: '{{name}} (copy)',
    setBaseline: 'Set as baseline',
    baseline: 'Baseline',
    delete: 'Delete variant',
    deleted: 'Deleted variant “{{name}}”',
    noItems: 'No items yet',
    expand: 'Expand {{name}}',
    collapse: 'Collapse {{name}}',
    inCharts: 'Show {{name}} in charts',
    showInCharts: 'Show in charts and comparisons',
    hideFromCharts: 'Hide from charts and comparisons',
  },
  groups: {
    income: 'Income',
    expense: 'Expenses',
    event: 'One-off events',
    transfer: 'Savings & transfers',
    asset: 'Assets',
    liability: 'Liabilities',
    rule: 'Rules',
  },
  cadence: {
    month: '/mo',
    quarter: '/qtr',
    halfYear: '/6 mo',
    year: '/yr',
    once: 'once',
  },
  templates: {
    salary: 'Salary',
    rentReceived: 'Rent received',
    rentPaid: 'Rent',
    utilities: 'Utilities',
    livingCosts: 'Living costs',
    subscription: 'Subscription',
    bonus: 'Bonus',
    purchase: 'Purchase',
    inheritance: 'Inheritance',
    savings: 'Savings account',
    investment: 'Investments',
    property: 'Home',
    mortgage: 'Mortgage',
    consumerLoan: 'Consumer loan',
    incomeTax: 'Income tax',
    salaryTaxBg: 'Salary tax (Bulgaria)',
    savingsRule: 'Savings rule',
  },
  categories: {
    newIncome: 'New income category',
    newExpense: 'New expense category',
    addIncome: 'Income',
    addExpense: 'Expense',
    hint: 'Default inflation applies to items in the category that follow inflation.',
    kind: { income: 'Income', expense: 'Expense' },
    name: 'Category name',
    delete: 'Delete category',
    inUse: 'Used by items, so it can’t be deleted',
    inflation: '{{name}} inflation',
    salary: 'Salary',
    otherIncome: 'Other income',
    housing: 'Housing',
    food: 'Food',
    transport: 'Transport',
    utilities: 'Utilities',
    leisure: 'Leisure',
    debt: 'Debt',
    taxes: 'Taxes',
    otherExpense: 'Other',
  },
  itemActions: {
    copyTo: 'Copy to',
    copiedInto: 'Copied “{{name}}” to {{variant}}',
    menu: 'Item actions',
    duplicate: 'Duplicate',
    copyName: '{{name}} (copy)',
    shareWith: 'Also use in',
    sharedIn_one: 'Used in {{count}} variant',
    sharedIn_other: 'Used in {{count}} variants',
    shared: 'Shared',
    detach: 'Make independent copy',
    removeFromVariant: 'Remove from this variant',
    delete: 'Delete',
    deleteEverywhere: 'Delete from all variants',
    deleted: 'Deleted “{{name}}”',
    removed: 'Removed “{{name}}” from {{variant}}',
    sharedInto: 'Now also used in {{variant}}',
    detached: '“{{name}}” is now independent in {{variant}}',
  },
  diagnostics: {
    formulaCycle: 'This rule depends on itself and is ignored.',
    formulaSourceMissing: 'A source of this rule is not in this variant and counts as 0.',
    formulaNegativeClamped: 'This rule came out negative and was set to 0.',
    taxNegativeClamped:
      'Tax already withheld this year exceeds what is due; refunds aren’t modelled, so it was set to 0.',
    transferAccountMissing:
      'The target account is not in this variant, so this transfer is ignored.',
    transferAccountClosed: 'Some transfers happen while the account is not open and are skipped.',
    withdrawalCapped: 'Withdrawals exceed the account balance and are capped.',
    itemStartsAfterHorizon: 'Starts after the end of the simulation.',
    loanExtendsBeyondHorizon: 'Not fully repaid within the simulation.',
    paymentsBeyondHorizon:
      'Some instalments fall after the end of the simulation; until then the rest shows as owed.',
  },
  inspector: {
    impactNetWorth: 'Net worth by {{year}}',
    impactMinCash: 'Lowest cash',
    impactHint: 'Difference with vs without this item in this variant.',
    miniChartAria: '{{name}} over time',
    label: 'Inspector',
    close: 'Close inspector',
    usedIn: 'Used in',
    sections: {
      categories: 'Categories',
      insights: 'Over time',
      amount: 'Amount',
      timing: 'Timing',
      growth: 'Growth',
      classification: 'Classification',
      formula: 'Calculation',
      tax: 'Tax rules',
      deductions: 'Deductions',
      brackets: 'Brackets',
      breakdown: 'Breakdown',
      account: 'Account',
      loan: 'Loan',
      repayments: 'Early repayments',
      fees: 'Fees',
      general: 'General',
      inflation: 'Inflation',
      advanced: 'Advanced',
    },
  },
  fields: {
    inflation: 'Inflation',
    inflationHint:
      'Overrides the category default or global CPI. “Default” periods fall back to it.',
    categoryDefault: '{{name}} default',
    globalCpi: 'Global CPI',
    name: 'Name',
    direction: 'Direction',
    directionIn: 'Money in',
    directionOut: 'Money out',
    amount: 'Amount',
    frequency: 'Frequency',
    frequencies: {
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      semiannual: 'Every 6 months',
      yearly: 'Yearly',
      once: 'Once',
    },
    start: 'Starts',
    end: 'Ends',
    noEnd: 'Runs to the end',
    setEnd: 'Set an end',
    clearEnd: 'Remove end',
    growth: 'Growth',
    growthKinds: {
      none: 'Stays the same',
      indexed: 'Follows inflation',
      rate: 'Custom rate',
      steps: 'Step raises',
    },
    timing: 'Updates',
    timings: { monthly: 'Monthly', annual: 'Once a year' },
    raise: 'Raise',
    everyMonths: 'Every (months)',
    basis: 'Amount is in',
    basisToday: 'Today’s money',
    basisAtStart: 'Money at start',
    category: 'Category',
    noCategory: 'No category',
    account: 'Transfer account',
    noAccount: 'None — income or expense',
    openingBalance: 'Opening balance',
    opens: 'Opens',
    fundedFromCash: 'Paid from cash',
    fundedFromCashHint: 'Buying this uses cash (e.g. a home); otherwise you already own it.',
    instalments: 'Pay in instalments',
    instalmentsHint:
      'Part of the price now, the rest later (e.g. 20% at signing, 80% at completion). Until paid, the rest is owed to the seller, without interest.',
    addInstalment: 'Add instalment',
    removeInstalment: 'Remove instalment {{n}}',
    instalmentMonth: 'Instalment {{n}}: month',
    instalmentShare: 'Instalment {{n}}: share of the price',
    instalmentRest: '{{share}}, the rest',
    liquidate: 'Sell or cash out',
    liquidateAt: 'Cash out in',
    return: 'Annual return',
    principal: 'Amount borrowed',
    termYears: 'Term (years)',
    rate: 'Interest rate',
    amortization: 'Repayment',
    annuity: 'Equal payments',
    linear: 'Equal principal',
    disburse: 'Paid out to cash',
    disburseHint: 'A new loan whose money arrives in cash. Off for debt you already have.',
    upfrontFee: 'Upfront fee',
    monthlyFee: 'Monthly fee',
    addRepayment: 'Add repayment',
    removeRepayment: 'Remove repayment',
    repaymentMonth: 'Month',
    repaymentAmount: 'Amount',
    repaymentMode: 'Effect',
    reduceTerm: 'Shorter term',
    reducePayment: 'Lower payment',
    sources: 'Based on',
    addSource: 'Add source',
    removeSource: 'Remove source',
    add: 'Add',
    subtract: 'Subtract',
    totalIncome: 'Total income',
    totalExpenses: 'Total expenses',
    accountReturn: 'Returns of {{name}}',
    ruleKind: 'Rule type',
    ruleKinds: { percentOf: 'Percentage', tax: 'Tax' },
    percent: 'Percentage',
    floor: 'Minimum per month',
    cap: 'Maximum per month',
    noLimit: 'None',
    scheduled: 'Varies over time — the schedule editor arrives in a later update.',
    invalidNumber: 'Enter a number',
    outOfRange: 'Must be between {{min}} and {{max}}',
    required: 'Required',
    month: 'Month',
    year: 'Year',
  },
  tax: {
    preset: 'Rules',
    custom: 'Custom',
    presets: {
      'bg-employment-2026': 'Bulgaria — employment, 2026',
      'bg-interest-2026': 'Bulgaria — taxable interest, 2026',
    },
    presetHint: 'Figures checked on {{date}}. Picking rules replaces the values below.',
    customHint: 'Your own rules. Pick a preset to start from official figures.',
    sources: 'Official sources',
    contributions: 'Social contributions',
    newDeduction: 'Deduction {{n}}',
    addDeduction: 'Add deduction',
    removeDeduction: 'Remove {{name}}',
    noDeductions: 'Nothing is deducted before tax.',
    deductionName: 'Name',
    deductionRate: 'Rate',
    deductionFloor: 'Minimum base',
    deductionCap: 'Maximum base',
    perMonth: 'Monthly, before rate',
    emit: 'Also paid',
    emitHint: 'Contributions are paid as well, so they reduce cash.',
    bracketsHint: 'Annual taxable income. A single bracket is a flat tax.',
    bracketFrom: 'From, per year',
    bracketRate: 'Rate',
    bracketN: 'Bracket {{n}}',
    addBracket: 'Add bracket',
    removeBracket: 'Remove bracket {{n}}',
    allowance: 'Tax-free amount, per year',
    credits: 'Tax credits, per year',
    settlement: 'Withholding',
    settlements: { monthly: 'Each month alone', annualCumulative: 'Year to date' },
    settlementHint: '“Year to date” suits progressive brackets and irregular income.',
    indexThresholds: 'Thresholds',
    indexings: { none: 'Fixed', cpi: 'Follow inflation' },
    breakdownMonth: 'Month',
    gross: 'Taxed income',
    taxable: 'Taxable',
    incomeTax: 'Income tax',
    net: 'Net',
    effectiveRate: 'Effective rate',
    breakdownEmpty: 'Nothing is taxed in this month.',
    notDeducted: 'reduces taxable income only',
  },
  mc: {
    running: 'Monte Carlo {{percent}}',
    runningLabel: 'Monte Carlo progress',
    cancel: 'Cancel Monte Carlo run',
    done: '{{paths}} paths · {{seconds}} s',
    doneHint:
      'Random paths for inflation and returns, drawn from the project’s seed: the same seed always gives the same result.',
    error: 'Monte Carlo failed',
    median: 'Median',
    bandsCaption: 'Median with P10–P90 and P5–P95 bands',
    averagePath: 'Average path',
    inner: 'P10–P90',
    outer: 'P5–P95',
    range: '{{from}} – {{to}}',
    averagePathNote:
      'This view shows the average path (every rate at its mean). The Monte Carlo range is in Overview and Risk.',
    pending: 'Simulating paths…',
    noUncertainty:
      'Nothing in this plan is uncertain yet, so every path is the same. Make inflation or a return “Uncertain” to see a range.',
    openAssumptions: 'Open assumptions',
  },
  risk: {
    enableTitle: 'See the range of outcomes',
    enableBody:
      'Monte Carlo simulates thousands of random paths for inflation and returns. Switch it on to see ranges, probabilities and goals.',
    enable: 'Run Monte Carlo',
    metric: 'Measure',
    fanTitle: '{{metric}}: range of outcomes',
    fanAria: '{{metric}} for {{name}}: median with P10–P90 and P5–P95 bands',
    pCashNegative: 'Chance of negative cash',
    pCashNegativeAria: 'Share of paths with negative cash, per month, for each variant',
    inMonth: 'In the month',
    ever: 'At any point so far',
    histogramTitle: 'Net worth distribution',
    histogramAt: 'end of {{date}}',
    histogramAria: 'Net worth across {{paths}} paths at the end of {{date}} for {{name}}',
    histogramHint: 'Hover the range chart to pick a year.',
    bin: '{{from}} to {{to}}: {{share}} of paths',
    goals: 'Goals',
    goalsEmpty: 'No goals yet. Add one to see how likely it is.',
    addGoal: 'Add goal',
    goalLine: '{{metric}} of at least {{target}} by {{date}}',
    goalLineReal: '{{metric}} of at least {{target}} (today’s money) by {{date}}',
    goalChanceAria: '{{name}}: {{chance}} chance',
  },
  keyDates: {
    title: 'Key dates',
    description:
      'Name the dates your plan depends on, and tie other dates to them: moving a key date moves everything tied to it.',
    name: 'Key date name',
    newName: 'Key date {{n}}',
    month: 'Month',
    add: 'Add key date',
    remove: 'Remove {{name}}',
    untied: 'Nothing tied to it yet',
    tied_one: '{{count}} date tied to it',
    tied_other: '{{count}} dates tied to it',
    keyDate: 'Key date',
    offset: 'Months after the key date',
    monthsShort: 'mo',
    tie: 'Tie {{field}} to a key date',
    untie: 'Untie {{field}} from its key date',
    afterHorizon: 'After the end of the simulation, so the dates tied to it are left out.',
  },
  goals: {
    title: 'Goals',
    description: 'Targets whose probability Monte Carlo runs report.',
    name: 'Goal name',
    newName: 'Goal {{n}}',
    metric: 'Measure',
    metrics: { netWorth: 'Net worth', cash: 'Cash' },
    target: 'At least',
    by: 'By',
    real: 'In today’s money',
    add: 'Add goal',
    remove: 'Remove {{name}}',
    metOnAverage: 'Met on the average path',
    notMetOnAverage: 'Not met on the average path',
  },
  rate: {
    describeCpi: 'CPI {{offset}}',
    describeDistribution: '≈{{mean}} ±{{vol}}',
    kind: 'Rate type',
    kinds: {
      inherit: 'Default',
      fixed: 'Fixed',
      cpiRelative: 'Inflation +',
      distribution: 'Uncertain',
    },
    rate: 'Rate',
    offset: 'Above inflation',
    mean: 'Average',
    volatility: 'Volatility',
    volatilityHint: 'Used by Monte Carlo runs; deterministic runs use the average.',
  },
  assumptions: {
    title: 'Assumptions',
    description: 'Global settings shared by every variant.',
    start: 'Start month',
    horizon: 'Horizon (years)',
    startingCash: 'Starting cash',
    currency: 'Currency',
    cpi: 'Inflation (CPI)',
    overdraft: 'Overdraft rate',
    overdraftHint: 'Charged on negative cash. Leave at 0 for none.',
    seed: 'Random seed',
    mcPaths: 'Monte Carlo paths',
  },
  scenario: {
    title: 'Variant',
    name: 'Name',
    color: 'Colour',
    colorN: 'Colour {{n}}',
    baseline: 'Baseline variant',
    baselineHint: 'Other variants are compared against the baseline.',
    items_one: '{{count}} item',
    items_other: '{{count}} items',
  },
  charts: {
    viewAsTable: 'View as table',
    viewAsChart: 'View as chart',
    year: 'Year',
    partialYear: '{{year}} (part year)',
    item: 'Item',
    total: 'Total',
    share: 'Share',
    other: 'Other',
    uncategorized: 'Uncategorised',
    income: 'Income',
    expenses: 'Expenses',
    incomeVsExpenses: 'Income vs expenses',
    incomeVsExpensesAria: 'Yearly income and expenses for {{name}}',
    cashFlowTitle: 'Cash flow by category',
    cashFlowAria:
      'Yearly income (above zero) and expenses (below zero) by category for {{name}}, with net cash flow',
    netFlow: 'Net cash flow',
    savingsRate: 'Savings rate',
    savingsRateAria: 'Share of income left after expenses, per year, for {{name}}',
    topItems: 'Largest items',
    balanceTitle: 'Assets and liabilities',
    balanceAria:
      'Cash, assets (above zero) and loans (below zero) over time for {{name}}, with net worth',
    allocation: 'Allocation',
    allocationAria: 'Share of holdings in {{date}}',
    debtTitle: 'Debt by loan',
    debtAria: 'Outstanding loan balances over time for {{name}}',
    interestVsPrincipal: 'Interest vs principal',
    interestVsPrincipalAria: 'Yearly interest and principal repaid for {{name}}',
    interest: 'Interest',
    principal: 'Principal',
    noDebt: '{{name}} has no loans.',
    range: 'Time range',
    rangeStart: 'Range start',
    rangeEnd: 'Range end',
    resetRange: 'Show all years',
    events: {
      loanPaidOff: '{{name}} paid off',
      firstNegativeCash: 'Cash turns negative',
      assetLiquidated: '{{name}} cashed out',
    },
    netWorth: 'Net worth',
    cash: 'Cash',
    owedFor: '{{name}}: owed to the seller',
    cashAria: 'Cash balance over time; months with negative cash are shaded',
    legend: 'Variants',
    viewSoon: 'This view arrives in a later update.',
    noData: 'Add a variant with a few items to see charts.',
    inMoney: 'in {{year}} money',
    negative: 'Negative cash',
  },
  schedule: {
    varyOverTime: 'Vary over time',
    title: '{{label}} over time',
    description: 'Each period applies from its start month until the next one begins.',
    summary_one: '{{count}} period: {{from}}',
    summary_other: '{{count}} periods: {{from}} → {{to}}',
    from: 'From',
    value: 'Value',
    actions: 'Actions',
    period: 'Period {{n}}',
    fromStart: 'Start ({{date}})',
    split: 'Split period',
    remove: 'Remove period',
    add: 'Add period',
    preview: 'Preview',
    previewAria: '{{title}}: preview',
    useSingle: 'Use one value',
    confirmSingle: 'Keep only the first period’s value?',
    save: 'Save',
    errors: {
      duplicate: 'Two periods start in the same month',
      outOfRange: 'Starts after the end of the simulation',
    },
  },
  compare: {
    baseline: 'Baseline',
    emptyTitle: 'Compare variants',
    emptyBody:
      'Compare needs a baseline and at least one other variant shown in charts. Duplicate a variant, change something and see the difference.',
    duplicate: 'Duplicate {{name}} to compare',
    storyTitle: 'Summary',
    ahead: '{{name}} ends {{amount}} ahead of {{baseline}} in {{year}}.',
    behind: '{{name}} ends {{amount}} behind {{baseline}} in {{year}}.',
    level: '{{name}} ends level with {{baseline}} in {{year}}.',
    overtakes: 'It moves ahead for good in {{date}}.',
    fallsBehind: 'It falls behind for good in {{date}}.',
    throughoutAhead: 'It is ahead the whole time.',
    throughoutBehind: 'It is behind the whole time.',
    mcBeats:
      'Ahead on {{chance}} of paths in {{year}}; median difference {{median}} (P10–P90: {{from}} to {{to}}).',
    overlayTitle: '{{metric}} by variant',
    diffTitle: 'Difference from {{baseline}}',
    diffCaption: 'Net worth minus the baseline’s; above zero is ahead',
    diffAria: 'Net worth of each variant minus {{baseline}}’s, over time',
    pBeatsTitle: 'Chance of beating {{baseline}}',
    pBeatsAria:
      'Share of paths on which each variant’s net worth exceeds {{baseline}}’s, per month',
    movesAhead: '{{name}} moves ahead',
    fallsBehindEvent: '{{name}} falls behind',
    metricsTitle: 'Key figures',
    figure: 'Figure',
    vsBaseline: '{{delta}} vs baseline',
    rows: {
      netWorthEnd: 'Net worth, {{year}}',
      lowestCash: 'Lowest cash',
      debtFree: 'Debt-free',
      interest: 'Interest paid',
      income: 'Total income',
      expenses: 'Total expenses',
      medianNetWorth: 'Median net worth, {{year}}',
      pCashNegative: 'Chance cash goes negative',
      pBeats: 'Chance of beating the baseline',
    },
  },
  kpi: {
    medianNetWorthEnd: 'Median net worth, {{year}}',
    p10p90: 'P10–P90: {{from}} – {{to}}',
    pCashNegative: 'Chance cash goes negative',
    pCashNegativeDetail: 'at some point, across {{paths}} paths',
    label: 'Key figures for {{name}}',
    netWorthEnd: 'Net worth, {{year}}',
    vsBaseline: '{{delta}} vs {{name}}',
    lowestCash: 'Lowest cash',
    inMonth: 'in {{date}}',
    debtFree: 'Debt-free',
    noDebt: 'No debt',
    notWithinHorizon: 'Not within the horizon',
    interestPaid: 'Interest paid',
    nominal: 'Nominal, whole horizon',
  },
  returns: {
    analyse: 'Analyse returns',
    open: 'Open returns',
    section: 'Returns',
    sectionHint: 'IRR, profit and multiple of this investment, as if it were sold in any month.',
    investment: 'Investment',
    investmentHint: 'Counted in the Returns view.',
    noInvestment: 'None',
    settings: 'Investment settings',
    name: 'Investment name',
    scope: 'What it includes',
    scopes: { variant: 'Whole variant', items: 'Selected items' },
    scopeHints: {
      variant:
        'Everything in the variant counts. Use this when the project holds only the investment: money it needs comes out of cash, and what it earns goes back.',
      items: 'Only the items switched on below count (shown for {{name}}).',
    },
    itemsLabel: 'Items in this investment',
    add: 'New investment',
    newName: 'Investment {{n}}',
    remove: 'Remove analysis',
    kpiLabel: 'Returns of {{name}}',
    irr: 'IRR',
    irrHint:
      'The yearly return on your own money, timing included: money put in early counts for more.',
    profit: 'Profit',
    profitHint:
      'Everything taken out, plus what a sale would bring after repaying the loans, minus everything put in.',
    multiple: 'Multiple',
    multipleHint:
      'Money taken out, sale included, for every 1 put in. ×2 means your money doubled.',
    multipleDetail: 'money back ÷ money in',
    peak: 'Most money tied up',
    peakHint: 'The most of your own money the investment has needed at any point so far.',
    peakDetail: 'of your own money, at the worst point',
    ifSoldIn: 'if sold in {{date}}, before selling costs',
    afterSale: 'sold by {{date}}',
    profitTitle: 'Profit if sold',
    irrTitle: 'IRR if sold',
    moneyTitle: 'Your money',
    cumulative: 'Taken out − put in',
    equity: 'Equity (assets − loans)',
    profitAria: 'Profit if sold, at the end of the range: {{values}}',
    irrAria: 'IRR if sold, at the end of the range: {{values}}',
    moneyAria:
      'Your money in {{name}} at the end of the range: taken out minus put in {{cumulative}}, equity {{equity}}',
  },
  explain: {
    title: 'What happened in {{date}}',
    description: 'Every cash movement in {{name}} this month.',
    opening: 'Cash at start of month',
    closing: 'Cash at end of month',
    noMovements: 'No cash movements this month.',
    goToItem: 'Open {{name}}',
    previous: 'Previous month',
    next: 'Next month',
    nominalNote: 'Nominal amounts',
    assets: 'Assets',
    liabilities: 'Liabilities',
    netWorth: 'Net worth',
  },
  ledger: {
    title: 'Ledger — {{name}}',
    nominalHint: 'Nominal amounts. Flows are summed per period; balances are at period end.',
    exportCsv: 'Export CSV',
    expandYear: 'Show months of {{year}}',
    collapseYear: 'Hide months of {{year}}',
    overdraft: 'Overdraft interest',
    netCashFlow: 'Net cash flow',
    cashEnd: 'Cash',
    assetsEnd: 'Assets',
    liabilitiesEnd: 'Liabilities',
    netWorthEnd: 'Net worth',
    partialNote: '* Part year (the simulation starts or ends during it).',
  },
  visibility: {
    mute: 'Mute',
    unmute: 'Unmute',
    solo: 'Solo',
    unsolo: 'Unsolo',
    muted: 'Muted',
    soloed: 'Solo',
    whatIf: 'What-if active',
    whatIfHint: 'Muted or soloed items are left out of the simulation. Click to reset.',
    reset: 'Reset',
  },
  shared: {
    title: 'Shared items',
    description: 'Items used by more than one variant. Editing one changes it everywhere.',
    emptyTitle: 'No shared items',
    emptyBody: 'Use “Also use in” on an item, or drag it onto another variant, to share it.',
  },
  status: {
    saved: 'Saved',
    unsaved: 'Unsaved changes',
    saving: 'Saving…',
    error: 'Not saved',
    errorHint: 'Saving to this browser failed. Your changes are kept while this tab is open.',
    calculated: '{{ms}} ms',
    calculatedHint: 'Simulation time',
    calculating: 'Calculating…',
    simError: 'Simulation failed',
    local: 'Stored in this browser',
  },
  history: {
    undo: 'Undo',
    redo: 'Redo',
  },
  account: {
    signIn: 'Sign in',
    signInToSync: 'Sign in to sync',
    signInHint: 'Keep your projects in your account and open them on any device.',
    menu: 'Account',
    signedInAs: 'Signed in as {{email}}',
    signOut: 'Sign out',
  },
  signIn: {
    title: 'Sign in to Kasora',
    body: 'Your projects sync across devices. Without an account, everything stays in this browser.',
    google: 'Continue with Google',
    github: 'Continue with GitHub',
    email: 'Email',
    sendLink: 'Email me a sign-in link',
    sending: 'Sending…',
    or: 'or',
    checkTitle: 'Check your email',
    checkBody:
      'We sent a sign-in link to {{email}}. It works once, for 15 minutes, in this browser.',
    useDifferent: 'Use a different email',
    unavailable: 'Accounts aren’t available here. Your projects stay in this browser.',
    back: 'Back to projects',
    verifying: 'Signing you in…',
    errors: {
      rate_limited: 'Too many sign-in emails. Please wait a few minutes and try again.',
      bad_request: 'Enter a valid email address.',
      link_invalid: 'This sign-in link has been used or isn’t valid. Request a new one.',
      link_expired: 'This sign-in link has expired. Request a new one.',
      link_other_browser:
        'Open the link in the browser where you asked for it, or request a new one here.',
      oauth_failed: 'Signing in with that provider didn’t work. Please try again.',
      email_unverified: 'That account has no verified email address.',
      provider_unavailable: 'That sign-in method isn’t available.',
      email_unavailable: 'Email sign-in isn’t available right now.',
      generic: 'Something went wrong. Please try again.',
    },
  },
  sync: {
    synced: 'Synced',
    syncedHint: 'Saved to your account',
    pending: 'Saving…',
    syncing: 'Syncing…',
    offline: 'Offline',
    offlineHint: 'Changes are saved in this browser and sync when you’re back online.',
    conflict: 'Conflict',
    localOnly: 'This browser only',
    localOnlyHint: 'Not in your account. Import it from the projects page to sync it.',
    conflictTitle: 'Changed on another device',
    conflictBody:
      '“{{name}}” was saved on another device {{time}}, after you started editing here. Which version do you want to keep?',
    keepMine: 'Keep mine',
    keepMineHint: 'Replace the other version with this one',
    takeTheirs: 'Use the other version',
    takeTheirsHint: 'Discard the changes made here',
    keepBoth: 'Keep both',
    keepBothHint: 'Use the other version and save yours as a copy',
    conflictCopy: '{{name}} (my copy)',
    importTitle: 'Add these projects to your account?',
    importBody: 'They’re only in this browser. Import them to open them on your other devices.',
    import_one: 'Import {{count}} project',
    import_other: 'Import {{count}} projects',
    notNow: 'Not now',
  },
  share: {
    button: 'Share',
    hint: 'Share a view-only link to this project',
    palette: 'Share…',
    title: 'Share “{{name}}”',
    description:
      'Anyone with the link can view this project (its variants, items and charts) without an account, but can’t change it.',
    loading: 'Checking for a link…',
    none: 'This project has no link yet.',
    create: 'Create link',
    link: 'Share link',
    copy: 'Copy',
    copied: 'Link copied',
    copyFailed: 'Couldn’t copy the link. Select it and copy it yourself.',
    language: 'Language',
    languageViewer: 'The viewer’s language',
    includeView: 'Open at the current view',
    includeViewHint: 'The link opens on this view, with the same variant, item and time range.',
    latestNote:
      'The link always shows the latest saved version. The project’s name appears in link previews.',
    reset: 'Reset link',
    stop: 'Stop sharing',
    confirmReset: 'The current link will stop working.',
    confirmStop: 'The link will stop working. You can share again later with a new link.',
    signInFirst: 'Sign in to share: the link shows the copy saved in your account.',
    localOnly: 'This project is only in this browser. Add it to your account to share it.',
    errors: {
      offline: 'You’re offline. Try again when you’re back online.',
      notUploaded: 'The project is still being saved to your account. Try again in a moment.',
      failed: 'Something went wrong. Try again.',
    },
  },
  viewer: {
    viewOnly: 'View only',
    viewOnlyHint:
      'A shared project: explore it as you like, but it can’t be changed here. Save a copy to edit it.',
    saveCopy: 'Save a copy',
    saveCopyHint: 'Copy this project to your projects and edit it there',
    copied: 'Saved a copy of “{{name}}” to your projects',
    documentTitle: '{{name}} · Kasora',
    loading: 'Opening the shared project…',
    reload: 'Reload',
    openKasora: 'Open Kasora',
    problems: {
      notFound: {
        title: 'This link no longer works',
        body: 'The owner may have stopped sharing the project, reset the link or deleted the project.',
      },
      outdated: {
        title: 'Kasora has been updated',
        body: 'This project was saved by a newer version of Kasora. Reload the page to open it.',
      },
      error: {
        title: 'Couldn’t open the project',
        body: 'Check your connection and try again.',
      },
    },
  },
  compact: {
    readOnly: 'Read-only on small screens. Open Kasora on a larger screen to edit.',
  },
  palette: {
    title: 'Command palette',
    placeholder: 'Type a command or search…',
    empty: 'No matching commands.',
    groups: {
      actions: 'Actions',
      goTo: 'Go to',
      view: 'View',
      preferences: 'Preferences',
      help: 'Help',
    },
    newItem: 'New item: {{name}}',
    goToView: 'Go to {{name}}',
    variant: 'Variant: {{name}}',
    item: '{{name}} · {{variant}}',
    showReal: 'Show real values (today’s money)',
    showNominal: 'Show nominal values',
    runMonteCarlo: 'Run Monte Carlo',
    deterministic: 'Back to deterministic',
    theme: 'Theme: {{name}}',
    language: 'Language: {{name}}',
    tour: 'Take the tour',
    projects: 'Go to projects',
  },
  shortcuts: {
    title: 'Keyboard shortcuts',
    then: 'then',
    or: 'or',
    groups: { general: 'General', navigation: 'Navigation', editing: 'Editing', view: 'View' },
    items: {
      palette: 'Command palette',
      help: 'Keyboard shortcuts',
      moveDown: 'Next item',
      moveUp: 'Previous item',
      open: 'Open',
      views: 'Views',
      toggleVariants: 'Variants panel',
      toggleInspector: 'Inspector',
      assumptions: 'Assumptions',
      createItem: 'New item',
      newVariant: 'New variant',
      editValue: 'Edit the item’s main value',
      duplicate: 'Duplicate',
      delete: 'Delete (with undo)',
      mute: 'Mute',
      solo: 'Solo',
      undo: 'Undo',
      redo: 'Redo',
      realNominal: 'Real / nominal',
      monteCarlo: 'Monte Carlo on / off',
      exportReport: 'Export PDF report',
    },
  },
  tour: {
    label: 'Tour, step {{step}} of {{total}}',
    tree: {
      title: 'Your variants',
      body: 'Each variant is a version of your plan. Its items — income, expenses, accounts, loans and rules — are listed here. Press C to add one.',
    },
    charts: {
      title: 'The charts',
      body: 'Every change recalculates instantly. Switch views with 1–7, and click a month to see what happened in it.',
    },
    palette: {
      title: 'Everything from the keyboard',
      body: 'Press ⌘K (Ctrl+K) for the command palette and ? for all shortcuts.',
    },
    next: 'Next',
    done: 'Done',
    skip: 'Skip tour',
  },
  home: {
    title: 'Projects',
    newProject: 'New project',
    emptyTitle: 'Plan your finances',
    emptyBody: 'Start from a template or a blank plan. Everything stays in this browser.',
    edited: 'Edited {{time}}',
    variants_one: '{{count}} variant',
    variants_other: '{{count}} variants',
    open: 'Open',
    actions: 'Project actions',
    delete: 'Delete project',
    deleteTitle: 'Delete “{{name}}”?',
    deleteBody: 'This removes the project from this browser. This cannot be undone.',
    sampleName: '{{name}} (sample)',
    deleteBodySynced:
      'The project moves to your account’s trash, where you can restore it for 30 days.',
    localOnly: 'This browser only',
    addToAccount: 'Add to account',
    trash: 'Trash',
    trashHint: 'Deleted projects can be restored for 30 days.',
    trashEmpty: 'The trash is empty.',
    restore: 'Restore',
    deleted: 'Deleted {{time}}',
    exportJson: 'Export as JSON',
    importJson: 'Import JSON',
    importFailed: 'That file isn’t a Kasora project.',
    imported: 'Imported “{{name}}”',
  },
  createProject: {
    title: 'New project',
    description: 'Pick a starting point. You can change everything later.',
    name: 'Project name',
    defaultName: 'My plan',
    create: 'Create project',
    templates: {
      blank: { title: 'Blank', body: 'One empty variant and default assumptions.' },
      rentVsBuy: {
        title: 'Rent vs buy',
        body: 'Two variants with the same income: renting or buying a home with a mortgage.',
      },
      retireEarly: {
        title: 'Retire at 55',
        body: 'Stop working at 55 or at 60 and live off your investments until the state pension.',
      },
      buyToLet: {
        title: 'Buy to let off-plan',
        body: 'A flat bought off-plan with a mortgage, let out and sold after 10 years, against a deposit. Returns analysed.',
      },
      earlyPayoff: {
        title: 'Pay off a mortgage early',
        body: 'The same mortgage paid on schedule or with yearly lump sums.',
      },
    },
    items: {
      investing: 'Investing',
      homeUpkeep: 'Home upkeep',
      purchaseCosts: 'Purchase costs',
      pension: 'State pension',
      drawdown: 'Living off investments',
      furnishing: 'Finishing and furnishing',
      runningCosts: 'Running costs and vacancy',
      sellingCosts: 'Selling costs',
      deposit: 'Deposit',
      flat: 'Flat to let',
    },
    keyDates: {
      completion: 'Building finished',
      sale: 'Sale',
    },
    variants: {
      plan: 'Plan',
      rent: 'Rent',
      buy: 'Buy',
      onSchedule: 'On schedule',
      lumpSums: 'Yearly lump sums',
      at55: 'Retire at 55',
      at60: 'Retire at 60',
      offPlan: 'Buy off-plan',
      deposit: 'Keep in a deposit',
    },
  },
  errors: {
    projectNotFound: 'That project could not be found.',
    projectInvalid: 'That project could not be opened.',
    futureVersion: 'That project was saved by a newer version of Kasora.',
    commandRejected: 'That change isn’t valid.',
    commandRejectedFor: 'That change isn’t valid: it would break {{name}}.',
    chartFailed: 'This chart couldn’t be drawn.',
    viewFailed: 'This view couldn’t be shown.',
    appFailed: 'Something went wrong',
    appFailedBody: 'Your work is saved in this browser. Reloading usually fixes this.',
    tryAgain: 'Try again',
    reload: 'Reload',
  },
  settings: {
    title: 'Settings',
    theme: 'Theme',
    themeDark: 'Dark',
    themeLight: 'Light',
    themeSystem: 'System',
    language: 'Language',
    languageEn: 'English',
    languageBg: 'Български',
  },
  panels: {
    toggleVariants: 'Toggle variants panel',
    toggleInspector: 'Toggle inspector',
    resize: 'Resize panel',
  },
  report: {
    dialog: {
      button: 'Report',
      hint: 'Export a PDF report of this plan',
      palette: 'Export PDF report',
      title: 'Export PDF report',
      description: 'A report of this plan, made on this device.',
      variants: 'Variants',
      noVariants: 'Choose at least one variant.',
      sections: 'Sections',
      sectionNames: {
        summary: 'Summary',
        assumptions: 'Assumptions',
        variants: 'A section per variant',
        comparison: 'Comparison',
        risk: 'Range of outcomes (Monte Carlo)',
        appendix: 'Appendix: year by year, method and sources',
      },
      comparisonNeedsTwo: 'Needs two or more variants.',
      riskNeedsMc: 'Needs a Monte Carlo run.',
      runMc: 'Run Monte Carlo',
      riskRunning: 'Monte Carlo is running…',
      values: 'Values',
      language: 'Language',
      pageSize: 'Page size',
      letter: 'Letter',
      overrides:
        'Muted or soloed items are left out of the report, as in the charts. Reset them to report the saved plan.',
      export: 'Export PDF',
      progress: 'Report progress',
      preparing: 'Preparing the report…',
      rendering: 'Laying out pages… {{percent}}',
      cancelExport: 'Stop',
      failed: 'The report couldn’t be created. Try again.',
      saved: 'Saved {{name}}',
    },
    subject: 'Financial plan for {{names}}',
    contents: 'Contents',
    continued: '(continued)',
    /** Filled in by the PDF layout ({page}, {pages}), not by i18next. */
    pageOf: 'Page {page} of {pages}',
    realMoney: 'Amounts are in {{year}} money (adjusted for inflation)',
    nominalMoney: 'Amounts are nominal (as they will appear in each year)',
    /** Short form for chart and table captions. */
    nominalCaption: 'Nominal amounts',
    realCaption: 'In {{year}} money',
    years_one: '{{years}} year',
    years_other: '{{years}} years',
    none: 'None',
    fromDate: '{{value}} from {{date}}',
    period: '{{from}} – {{to}}',
    periodOpen: 'From {{from}}',
    inflation: 'Inflation',
    steps_one: '{{raise}} every month',
    steps_other: '{{raise}} every {{count}} months',
    loanRate_one: '{{rate}} over {{years}} year',
    loanRate_other: '{{rate}} over {{years}} years',
    cover: {
      eyebrow: 'Financial plan report',
      subtitle: '{{from}} – {{to}} · {{years}}',
      variants: 'Variants',
      values: 'Values',
      simulation: 'Simulation',
      monteCarlo: 'Monte Carlo, {{paths}} paths',
      date: 'Prepared',
      note: 'Made with Kasora entirely in your browser: no plan data was sent anywhere to create this report. The figures are projections from the assumptions in this plan, not predictions or financial advice.',
    },
    summary: {
      title: 'Summary',
      intro_one: 'This report covers {{names}} over {{period}}, from {{from}} to {{to}}.',
      intro_other:
        'This report compares {{count}} variants, {{names}}, over {{period}}, from {{from}} to {{to}}.',
      mc: 'Ranges come from {{paths}} Monte Carlo paths.',
      verdict: 'How the variants compare',
    },
    goals: {
      goal: 'Goal',
      target: 'Target',
      met: 'Met',
      notMet: 'Not met',
      mcCaption: 'Chance of meeting each goal, across {{paths}} paths',
      averageCaption: 'On the average path, with every rate at its mean',
    },
    assumptions: {
      intro: 'Every variant shares these settings.',
      setting: 'Setting',
      value: 'Value',
      keyDates: 'Key dates',
      horizon: 'Horizon',
      values: 'Values',
      simulation: 'Simulation',
      monteCarlo:
        'Monte Carlo: {{paths}} random paths for inflation and returns, seed {{seed}}. The same seed always gives the same result.',
    },
    variant: {
      title: 'Variant: {{name}}',
      items: 'Items',
      type: 'Type',
      amount: 'Amount',
      when: 'When',
      rate: 'Growth or rate',
    },
    comparison: {
      title: 'Comparison',
      intro:
        'Each variant is compared with the baseline, {{baseline}}. All variants share the same assumptions and, in Monte Carlo, the same random paths, so the differences come from the choices in each variant, not from luck.',
    },
    risk: {
      title: 'Range of outcomes',
      intro:
        'Monte Carlo simulates {{paths}} random paths for inflation and returns (seed {{seed}}). Each chart shows the median path, the range that holds 8 in 10 paths (P10–P90) and the range that holds 9 in 10 (P5–P95).',
      noUncertainty:
        'Nothing in this plan is uncertain, so every path is the same and the ranges collapse onto one line.',
      fanTitle: 'Net worth, {{name}}: range of outcomes',
      tableTitle: 'Outcomes in {{year}}',
      variant: 'Variant',
    },
    appendix: {
      title: 'Appendix',
      yearly: 'Year by year: {{name}}',
      yearlyCaption:
        'Income and expenses are summed per calendar year; cash and net worth are at the end of the year.',
      cashEnd: 'Cash, year end',
      netWorthEnd: 'Net worth, year end',
    },
    method: {
      title: 'How the numbers are calculated',
      time: 'The simulation runs month by month. Balances are at the end of each month, and a loan’s first payment is due the month after it starts.',
      growth:
        'Growth, returns and inflation compound monthly, so 12 months at an annual rate add exactly that rate. Amounts entered in today’s money are indexed from the start month.',
      loans:
        'Loans follow the lending convention: the monthly rate is the annual rate divided by 12. A variable rate recalculates the payment.',
      real: 'Real values divide nominal values by the inflation since the start, so they show amounts in start-year money.',
      tax: 'Taxes follow the rules entered in each tax item: contributions up to their cap, then tax on the rest, with the brackets and allowances shown there.',
      monteCarlo:
        'Monte Carlo draws one random value per factor and year, with year-to-year persistence where set. Every variant uses the same draws, so their differences reflect decisions, not luck. Percentiles are taken path by path.',
      rounding:
        'Calculations use full precision and amounts are rounded only for display, so a total can differ slightly from the sum of the rounded figures.',
    },
    sources: {
      title: 'Tax rules and sources',
      checked: '— figures checked against the official sources on {{date}}:',
    },
    disclaimer:
      'This report is a projection based on the assumptions and items in the plan. It is not financial, tax or legal advice. Actual results will differ, so check important decisions with a qualified adviser.',
  },
  help: {
    menu: 'Help',
    title: 'Kasora help',
    tabs: { welcome: 'Welcome', tutorial: 'Tutorial', shortcuts: 'Shortcuts' },
    menuItems: {
      welcome: 'What is Kasora?',
      tutorial: 'Tutorial',
      shortcuts: 'Keyboard shortcuts',
      tour: 'Take the tour',
    },
    welcome: {
      eyebrow: 'Personal financial simulator',
      headline: 'See where your money is heading',
      lead: 'Kasora plays your plan forward month by month: income, spending, savings, loans and taxes, with inflation. Compare choices like renting or buying before you make them, and see how sure you can be.',
      whyTitle: 'Why Kasora',
      features: {
        monthly: {
          title: 'Every month, decades ahead',
          body: 'Raises, rent, mortgages, investments and taxes, simulated month by month for up to 60 years, in today’s money or as it will appear.',
        },
        compare: {
          title: 'Compare choices side by side',
          body: 'Make a variant of your plan, change one thing and see the difference: where each ends up, and when one pulls ahead for good.',
        },
        uncertainty: {
          title: 'A range, not one guess',
          body: 'Monte Carlo runs thousands of possible futures for inflation and returns, then shows the range and the chance of reaching your goals.',
        },
        explain: {
          title: 'Every number explained',
          body: 'Click any month to see each movement of cash behind it. The ledger and table views show the rest, with nothing hidden.',
        },
        private: {
          title: 'Private by design',
          body: 'Everything is calculated in your browser. You don’t need an account; sign in only to sync between devices.',
        },
        share: {
          title: 'Ready to share',
          body: 'Export a polished PDF report, or send a read-only link to a partner or adviser.',
        },
      },
      differentTitle: 'What makes it different',
      different: {
        fair: {
          lead: 'Fair comparisons',
          body: 'Every variant faces the same random future, so differences come from your decisions, not luck.',
        },
        real: {
          lead: 'Real values',
          body: 'Inflation-adjusted amounts show what your money will actually buy.',
        },
        portable: {
          lead: 'Yours to keep',
          body: 'Export a plan as a file or a PDF report at any time; nothing locks you in.',
        },
        keyboard: {
          lead: 'Fast and accessible',
          body: 'Everything works from the keyboard and with screen readers.',
        },
      },
      tour: 'Take the tour',
      tourHint: 'Three quick stops: your variants, the charts and the command palette.',
      tutorial: 'Read the tutorial',
      explore: 'Start exploring',
    },
    tutorial: {
      steps: 'Tutorial steps',
      stepOf: 'Step {{n}} of {{total}}',
      back: 'Back',
      next: 'Next',
      done: 'Done',
      tryIt: 'Try it',
      projects: {
        title: 'Projects and variants',
        p1: 'A project is one plan: when it starts, how many years it looks ahead, your starting cash and the inflation you expect.',
        p2: 'Variants are versions of the plan, such as “Rent” and “Buy”. Items they have in common, like your salary, are shared, so you enter them once.',
      },
      items: {
        title: 'Add what comes in and goes out',
        p1: 'Items are the parts of your plan: income, spending, one-off events, savings and investments, loans, and rules such as a tax or “save 20 % of income”.',
        p2: 'Select an item to edit it in the inspector on the right. Amounts can follow inflation, grow at a rate or rise in steps.',
      },
      assumptions: {
        title: 'Set your assumptions',
        p1: 'Assumptions apply to every variant: the start month, the horizon, your starting cash, the currency and inflation.',
        p2: 'Any rate can change over time, such as a mortgage fixed for five years and then inflation plus 2 %. Choose “Vary over time” next to the field.',
      },
      charts: {
        title: 'Read the charts',
        p1: 'The views show net worth, cash flow, assets and debts, and debt paydown, as nominal amounts or as real ones in today’s money.',
        p2: 'Click a month, or press Enter on a focused chart, to see exactly what happened in it. Every chart can also be read as a table.',
      },
      compare: {
        title: 'Compare, and plan for uncertainty',
        p1: 'Compare shows how each variant does against the baseline: the difference over time, and the month one moves ahead for good.',
        p2: 'Switch on Monte Carlo to see a range of outcomes instead of a single path, the chance of running short of cash, and the odds of meeting your goals.',
      },
      share: {
        title: 'Save, share and report',
        p1: 'Your work saves as you go, in this browser. Sign in to sync it across your devices; nothing else changes.',
        p2: 'Share a read-only link, or export a PDF report with the charts, tables and the method behind the numbers.',
      },
    },
    shortcuts: { intro: 'Press ? anywhere to open this list.' },
  },
  common: {
    close: 'Close',
    comingSoon: 'Coming soon',
    cancel: 'Cancel',
    delete: 'Delete',
    undo: 'Undo',
    more: 'More',
    none: 'None',
  },
} as const;

type DeepStrings<T> = { [K in keyof T]: T[K] extends string ? string : DeepStrings<T[K]> };

export type Translation = DeepStrings<typeof en>;
